/**
 * Onboarding store — persistent state which tips have already been seen.
 *
 * Persisted to localStorage so подсказки не появляются повторно при
 * перезагрузке игры. `dismissed: Set<string>` хранится как массив в JSON.
 *
 * Текущая активная подсказка (`current`) — НЕ persisted. Перезагрузка
 * сбрасывает её и driver выберет заново через 1-2 сек если условие ещё
 * актуально (например first_sight снова покажет если игрок ни разу не
 * тапнул).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { emit } from '../telemetry/emit.js';
import type { OnboardingTip } from './types.js';
import { TIPS_BY_ID } from './tips.js';

/**
 * Когда tip впервые offered — фиксируем wall clock чтобы посчитать
 * duration на acknowledge/dismiss. Module-level Map потому что store
 * shape должен оставаться JSON-serializable для persist'а.
 */
const tipShownAt = new Map<string, number>();

interface OnboardingStore {
  /** IDs всех просмотренных и закрытых подсказок. Растёт навсегда. */
  readonly dismissed: ReadonlySet<string>;
  /** Текущая активная подсказка — не persisted. */
  readonly current: OnboardingTip | null;

  /**
   * Если эта подсказка ещё не показывалась — поставить её активной.
   * Возвращает true если подсказка показана/уже показана этой сессии.
   */
  offerTip(tipId: string): boolean;
  /** Принять подсказку (нажал «понятно») → переход к следующему шагу или закрытие. */
  acknowledge(): void;
  /** Закрыть текущую без шагов — сразу dismiss. */
  dismiss(): void;
  /** Сбросить все подсказки — для дев-консоли / Settings. */
  resetAll(): void;
}

/**
 * Zustand store с persist. Дублируем dismissed как массив для JSON.
 */
interface PersistedShape {
  readonly dismissed: readonly string[];
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      dismissed: new Set<string>(),
      current: null,

      offerTip(tipId: string) {
        const tip = TIPS_BY_ID.get(tipId);
        if (tip === undefined) return false;
        const { dismissed, current } = get();
        if (dismissed.has(tipId)) return false;
        // If something else is open with higher priority — leave it.
        if (current !== null) {
          const currentPri = current.priority ?? 1;
          const newPri = tip.priority ?? 1;
          if (newPri <= currentPri) return false;
        }
        set({ current: tip });
        // Analytics: tip впервые показан (`offerTip` гарантирует что
        // tipId не был ранее dismissed). Записываем wall clock для
        // последующего duration в acknowledge/dismiss.
        tipShownAt.set(tipId, Date.now());
        emit('onboarding.shown', { funnel_step: tipId });
        return true;
      },

      acknowledge() {
        const { current, dismissed } = get();
        if (current === null) return;
        // For multi-step tips we don't track step in the store — the UI
        // component holds the local step index and only calls `acknowledge`
        // on the FINAL step. So acknowledge always = dismiss.
        const nextDismissed = new Set(dismissed);
        nextDismissed.add(current.id);
        // Analytics: явный «понятно» = completed (different from dismiss).
        const shownAt = tipShownAt.get(current.id);
        const duration_sec = shownAt !== undefined ? Math.round((Date.now() - shownAt) / 1000) : 0;
        tipShownAt.delete(current.id);
        emit('onboarding.completed', { duration_sec, skipped_steps: 0 });
        set({ current: null, dismissed: nextDismissed });
      },

      dismiss() {
        const { current, dismissed } = get();
        if (current === null) return;
        const nextDismissed = new Set(dismissed);
        nextDismissed.add(current.id);
        // Analytics: abandoned (close без acknowledge — крестик или
        // outside click). Отличается от completed для funnel analysis.
        const shownAt = tipShownAt.get(current.id);
        const duration_sec = shownAt !== undefined ? Math.round((Date.now() - shownAt) / 1000) : 0;
        tipShownAt.delete(current.id);
        emit('onboarding.abandoned', { last_step: current.id, duration_sec });
        set({ current: null, dismissed: nextDismissed });
      },

      resetAll() {
        set({ current: null, dismissed: new Set<string>() });
      },
    }),
    {
      name: 'sai-onboarding-v1',
      partialize: (s): PersistedShape => ({
        dismissed: Array.from(s.dismissed),
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PersistedShape> | undefined;
        const arr = persisted?.dismissed ?? [];
        return {
          ...currentState,
          dismissed: new Set(arr),
        };
      },
    },
  ),
);
